import React, { Suspense, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ImageOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type {
  ClientPortalBreakdownRow,
  ClientPortalCreative,
  ClientPortalDetail,
  ClientPortalDetailDay,
  ClientPortalPlacement,
} from "../../store/apis/clientPortalApi";
import { ClientCard } from "./ClientUiViewClient";
import {
  costPer,
  formatCompact,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  formatUnitMoney,
  metricSeriesColor,
} from "./clientFormatViewClient";

const ClientMapViewClient = React.lazy(() => import("./ClientMapViewClient"));

// Apartados de detalle de una subcampaña o de una campaña entera: lo mismo que
// el detalle interno de campañas, pero solo con lo que puede ver el cliente
// (la inversión con margen, nunca el coste real). Cada apartado aparece solo si
// tiene algo que contar.

const ratio = (part: number, total: number | null | undefined) =>
  total && total > 0 ? part / total : null;

// ─── Piezas ──────────────────────────────────────────────────────────────────

/** Cifra pequeña dentro de un grupo, con su explicación debajo. */
const MiniStat: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "16px",
        backgroundColor: theme.palette.neutral[100],
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.75 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

const GroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="h3"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const statGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
  gap: 1.5,
} as const;

/** Barra fina de la parte de un total. */
const ShareBar: React.FC<{ value: number }> = ({ value }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: 6,
        borderRadius: 999,
        backgroundColor: theme.palette.neutral[200],
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${Math.min(Math.max(value, 0), 1) * 100}%`,
          height: "100%",
          borderRadius: 999,
          backgroundColor: metricSeriesColor(
            "impressions",
            0,
            theme.palette.mode
          ),
        }}
      />
    </Box>
  );
};

/**
 * Lista con barras: cada fila, su parte del total de impresiones, su CTR y,
 * con `currency`, su inversión.
 */
export const BarList: React.FC<{
  rows: {
    key: string;
    label: string;
    detail?: string | null;
    impressions: number;
    clicks: number;
    investment?: number;
  }[];
  initial?: number;
  /** false: clics en vez de CTR */
  withCtr?: boolean;
  currency?: string;
}> = ({ rows, initial = rows.length, withCtr = true, currency }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [expanded, setExpanded] = useState(false);
  const total = rows.reduce((sum, r) => sum + r.impressions, 0);
  const shown = expanded ? rows : rows.slice(0, initial);
  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {shown.map((row) => {
          const share = ratio(row.impressions, total) ?? 0;
          const ctr = ratio(row.clicks, row.impressions);
          return (
            <Box key={row.key} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 0.75,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row.label}
                  >
                    {row.label}
                  </Typography>
                  {row.detail && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.detail}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: "text.primary", fontWeight: 700 }}
                  >
                    {formatPercent(share, language)}
                  </Box>
                  {" · "}
                  {formatNumber(row.impressions, language)}{" "}
                  {t("clientView.detail.impressionsShort")}
                  {withCtr &&
                    ctr !== null &&
                    row.clicks > 0 &&
                    ` · CTR ${formatPercent(ctr, language, 2)}`}
                  {!withCtr &&
                    row.clicks > 0 &&
                    ` · ${formatCompact(row.clicks, language)} ${t("clientView.detail.clicksShort")}`}
                  {currency &&
                    !!row.investment &&
                    ` · ${formatMoney(row.investment, currency, language)}`}
                </Typography>
              </Box>
              <ShareBar value={share} />
            </Box>
          );
        })}
      </Box>
      {rows.length > initial && (
        <Button
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
        >
          {expanded
            ? t("clientView.detail.showLess")
            : t("clientView.detail.showAll", { count: rows.length })}
        </Button>
      )}
    </Box>
  );
};

// ─── Resultados ──────────────────────────────────────────────────────────────

const ResultsSection: React.FC<{
  detail: ClientPortalDetail;
  currency: string;
}> = ({ detail, currency }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const s = detail.summary;
  const count = (v: number) => formatNumber(v, language);
  const pct = (v: number | null) =>
    v === null ? null : formatPercent(v, language, 1);
  const unit = (v: number | null) =>
    v === null ? null : formatUnitMoney(v, currency, language);

  const groups: {
    key: string;
    stats: { label: string; value: string | null; hint?: string }[];
  }[] = [
    {
      key: "reach",
      stats: s.uniqueUsers
        ? [
            {
              label: t("clientView.detail.uniqueUsers"),
              value: count(s.uniqueUsers),
              hint: t("clientView.detail.uniqueUsersHint"),
            },
            {
              label: t("clientView.detail.frequency"),
              value: s.frequency
                ? formatNumber(Math.round(s.frequency * 10) / 10, language)
                : null,
              hint: t("clientView.detail.frequencyHint"),
            },
          ]
        : [],
    },
    {
      key: "visibility",
      stats:
        s.measurableImpressions > 0
          ? [
              {
                label: t("clientView.detail.viewability"),
                value: pct(
                  ratio(s.viewableImpressions, s.measurableImpressions)
                ),
                hint: t("clientView.detail.viewabilityHint"),
              },
              {
                label: t("clientView.detail.viewableImpressions"),
                value: count(s.viewableImpressions),
              },
            ]
          : [],
    },
    // Los totales de vistas completas, conversiones e interacciones ya están
    // arriba: aquí van las cifras que los ponen en contexto.
    {
      key: "video",
      stats:
        s.completeViews > 0 || (s.videoViews ?? 0) > 0
          ? [
              {
                label: t("clientView.detail.videoViews"),
                value: s.videoViews ? count(s.videoViews) : null,
              },
              {
                label: t("clientView.detail.vtr"),
                value:
                  s.completeViews > 0
                    ? pct(ratio(s.completeViews, s.videoViews || s.impressions))
                    : null,
                hint: t(
                  s.videoViews
                    ? "clientView.detail.vtrHint"
                    : "clientView.detail.vtrImpressionsHint"
                ),
              },
              {
                label: t("clientView.money.costPerVideoView"),
                value: unit(costPer(s.investment, s.videoViews)),
                hint: t("clientView.money.costPerVideoViewHint"),
              },
            ]
          : [],
    },
    {
      key: "performance",
      stats: [
        {
          label: t("clientView.detail.conversionRate"),
          value: s.conversions > 0 ? pct(ratio(s.conversions, s.clicks)) : null,
          hint: t("clientView.detail.conversionRateHint"),
        },
        {
          label: t("clientView.detail.engagementRate"),
          value:
            s.engagements > 0 ? pct(ratio(s.engagements, s.impressions)) : null,
          hint: t("clientView.detail.engagementRateHint"),
        },
      ],
    },
    {
      key: "screens",
      stats: s.plays
        ? [
            { label: t("clientView.detail.plays"), value: count(s.plays) },
            {
              label: t("clientView.detail.impressionsPerPlay"),
              value: formatNumber(
                Math.round((s.impressions / s.plays) * 10) / 10,
                language
              ),
              hint: t("clientView.detail.impressionsPerPlayHint"),
            },
            {
              label: t("clientView.money.costPerPlay"),
              value: unit(costPer(s.investment, s.plays)),
            },
          ]
        : [],
    },
  ];
  const visible = groups
    .map((g) => ({ ...g, stats: g.stats.filter((st) => st.value !== null) }))
    .filter((g) => g.stats.length > 0);
  if (visible.length === 0) return null;

  return (
    <ClientCard
      title={t("clientView.detail.resultsTitle")}
      subtitle={t("clientView.detail.resultsSubtitle")}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
        {visible.map((group) => (
          <Box key={group.key}>
            <GroupTitle>
              {t(`clientView.detail.groups.${group.key}`)}
            </GroupTitle>
            <Box sx={statGridSx}>
              {group.stats.map((stat) => (
                <MiniStat
                  key={stat.label}
                  label={stat.label}
                  value={stat.value as string}
                  hint={stat.hint}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </ClientCard>
  );
};

// ─── Retención del vídeo ─────────────────────────────────────────────────────

const RetentionSection: React.FC<{ detail: ClientPortalDetail }> = ({
  detail,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const s = detail.summary;
  const base = s.videoViews || s.impressions;
  if (!s.firstQuartileViews || !s.completeViews || !base) return null;
  const steps = [
    ...(s.videoViews ? [{ key: "start", value: s.videoViews }] : []),
    { key: "q25", value: s.firstQuartileViews },
    { key: "q50", value: s.midpointViews },
    { key: "q75", value: s.thirdQuartileViews },
    { key: "q100", value: s.completeViews },
  ];

  return (
    <ClientCard
      title={t("clientView.detail.retentionTitle")}
      subtitle={t(
        s.videoViews
          ? "clientView.detail.retentionSubtitle"
          : "clientView.detail.retentionSubtitleImpressions"
      )}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
        {steps.map((step) => {
          const share = ratio(step.value, base) ?? 0;
          return (
            <Box
              key={step.key}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "160px minmax(0, 1fr) 150px",
                },
                alignItems: "center",
                columnGap: 2.5,
                rowGap: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {t(`clientView.detail.retention.${step.key}`)}
              </Typography>
              <ShareBar value={share} />
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  textAlign: { sm: "right" },
                }}
              >
                <Box
                  component="span"
                  sx={{ color: "text.primary", fontWeight: 700 }}
                >
                  {formatPercent(share, language)}
                </Box>
                {" · "}
                {formatCompact(step.value, language)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </ClientCard>
  );
};

// ─── Anuncios ────────────────────────────────────────────────────────────────

const CreativeCard: React.FC<{
  creative: ClientPortalCreative;
  currency: string;
}> = ({ creative, currency }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const theme = useTheme();
  const [broken, setBroken] = useState(false);
  const image = creative.thumbnailUrl && !broken ? creative.thumbnailUrl : null;
  // Los anuncios de texto (búsqueda) no tienen imagen: se enseña solo su texto.
  const textAd = !creative.thumbnailUrl && !!creative.description;
  const cpc = costPer(creative.investment, creative.clicks);

  return (
    <Box
      sx={{
        borderRadius: "18px",
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      {!textAd && (
        <Box
          sx={{
            aspectRatio: "16 / 9",
            backgroundColor: theme.palette.neutral[100],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {image ? (
            <Box
              component="img"
              src={image}
              alt={creative.name}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setBroken(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageOutlined
              aria-label={t("clientView.detail.noPreview")}
              sx={{ fontSize: 36, color: theme.palette.neutral[400] }}
            />
          )}
        </Box>
      )}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={creative.name}
        >
          {creative.name}
        </Typography>
        {creative.headline && creative.headline !== creative.name && (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {creative.headline}
          </Typography>
        )}
        {creative.description && (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={creative.description}
          >
            {creative.description}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 0.5,
            mt: 0.5,
            fontSize: 12,
            color: "text.secondary",
          }}
        >
          <span>
            <strong>{formatNumber(creative.impressions, language)}</strong>{" "}
            {t("clientView.detail.impressionsShort")}
          </span>
          {creative.clicks > 0 && (
            <span>
              <strong>{formatCompact(creative.clicks, language)}</strong>{" "}
              {t("clientView.detail.clicksShort")}
            </span>
          )}
          {creative.completeViews > 0 && (
            <span>
              <strong>{formatCompact(creative.completeViews, language)}</strong>{" "}
              {t("clientView.detail.completeViewsShort")}
            </span>
          )}
          {creative.conversions > 0 && (
            <span>
              <strong>{formatCompact(creative.conversions, language)}</strong>{" "}
              {t("clientView.detail.conversionsShort")}
            </span>
          )}
          {creative.investment > 0 && (
            <span>
              <strong>
                {formatMoney(creative.investment, currency, language)}
              </strong>
            </span>
          )}
          {cpc !== null && (
            <span>
              {t("clientView.money.cpc")}{" "}
              <strong>{formatUnitMoney(cpc, currency, language)}</strong>
            </span>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CreativesSection: React.FC<{
  creatives: ClientPortalCreative[];
  currency: string;
}> = ({ creatives, currency }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (creatives.length === 0) return null;
  const initial = 8;
  const shown = expanded ? creatives : creatives.slice(0, initial);
  return (
    <ClientCard
      title={t("clientView.detail.creativesTitle")}
      subtitle={t("clientView.detail.creativesSubtitle")}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 2,
        }}
      >
        {shown.map((creative) => (
          <CreativeCard
            key={creative.id}
            creative={creative}
            currency={currency}
          />
        ))}
      </Box>
      {creatives.length > initial && (
        <Button
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
        >
          {expanded
            ? t("clientView.detail.showLess")
            : t("clientView.detail.showAll", { count: creatives.length })}
        </Button>
      )}
    </ClientCard>
  );
};

// ─── Dónde se ha visto ───────────────────────────────────────────────────────

const PlacementsSection: React.FC<{
  placements: ClientPortalPlacement[];
  currency: string;
}> = ({ placements, currency }) => {
  const { t } = useTranslation();
  if (placements.length === 0) return null;
  return (
    <ClientCard
      title={t("clientView.detail.placementsTitle")}
      subtitle={t("clientView.detail.placementsSubtitle")}
    >
      <BarList
        initial={8}
        withCtr={false}
        currency={currency}
        rows={placements.map((p) => ({
          key: `${p.name}-${p.section ?? ""}`,
          label: p.name,
          detail: p.section,
          impressions: p.impressions,
          clicks: p.clicks,
          investment: p.investment,
        }))}
      />
    </ClientCard>
  );
};

// ─── Audiencia ───────────────────────────────────────────────────────────────

const AudienceSection: React.FC<{
  detail: ClientPortalDetail;
  currency: string;
}> = ({ detail, currency }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const panels = (
    [
      ["devices", detail.devices],
      ["ages", detail.ages],
      ["genders", detail.genders],
      ["audiences", detail.audiences],
    ] as [string, ClientPortalBreakdownRow[]][]
  ).filter(([, rows]) => rows.length > 0);
  if (panels.length === 0) return null;

  return (
    <ClientCard
      title={t("clientView.detail.audienceTitle")}
      subtitle={t("clientView.detail.audienceSubtitle")}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2.5,
        }}
      >
        {panels.map(([key, rows]) => (
          <Box
            key={key}
            sx={{
              p: 3,
              borderRadius: "18px",
              border: `1px solid ${theme.palette.divider}`,
              minWidth: 0,
            }}
          >
            <GroupTitle>{t(`clientView.detail.${key}`)}</GroupTitle>
            <BarList
              initial={6}
              currency={currency}
              rows={rows.map((r) => ({
                key: r.label,
                label: r.label,
                impressions: r.impressions,
                clicks: r.clicks,
                investment: r.investment,
              }))}
            />
          </Box>
        ))}
      </Box>
    </ClientCard>
  );
};

// ─── Mapa ────────────────────────────────────────────────────────────────────

const MapSection: React.FC<{ detail: ClientPortalDetail }> = ({ detail }) => {
  const { t } = useTranslation();
  if (detail.locations.length === 0) return null;
  return (
    <ClientCard
      title={t("clientView.detail.mapTitle")}
      subtitle={t("clientView.detail.mapSubtitle", {
        count: detail.locations.length,
      })}
    >
      <Suspense fallback={<Box sx={{ height: 420 }} />}>
        <ClientMapViewClient locations={detail.locations} />
      </Suspense>
    </ClientCard>
  );
};

// ─── Detalle por día ─────────────────────────────────────────────────────────

const DailyTableSection: React.FC<{
  detail: ClientPortalDetail;
  currency: string;
  googleAds?: boolean;
}> = ({ detail, currency, googleAds }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const theme = useTheme();
  if (detail.daily.length === 0) return null;
  const s = detail.summary;
  // Coste por unidad de cada día: "—" si ese día no hay con qué.
  const costColumn = (
    key: "cpm" | "cpc" | "cpa",
    units: (d: ClientPortalDetailDay) => number
  ) => ({
    key,
    label: t(`clientView.money.${key}`),
    value: (d: ClientPortalDetailDay) => {
      const cost = costPer(d.investment, units(d), key === "cpm");
      return cost === null ? "—" : formatUnitMoney(cost, currency, language);
    },
  });
  const costColumns = googleAds
    ? [
        ...(s.clicks > 0 ? [costColumn("cpc", (d) => d.clicks)] : []),
        ...(s.conversions > 0 ? [costColumn("cpa", (d) => d.conversions)] : []),
      ]
    : [
        costColumn("cpm", (d) => d.impressions),
        ...(s.clicks > 0 ? [costColumn("cpc", (d) => d.clicks)] : []),
      ];
  const columns: {
    key: string;
    label: string;
    value: (d: ClientPortalDetailDay) => string;
  }[] = [
    {
      key: "impressions",
      label: t("clientView.stats.impressions"),
      value: (d) => formatNumber(d.impressions, language),
    },
    {
      key: "clicks",
      label: t("clientView.stats.clicks"),
      value: (d) => formatNumber(d.clicks, language),
    },
    {
      key: "ctr",
      label: t("clientView.stats.ctr"),
      value: (d) => {
        const ctr = ratio(d.clicks, d.impressions);
        return ctr === null ? "—" : formatPercent(ctr, language, 2);
      },
    },
    ...(s.completeViews > 0
      ? [
          {
            key: "completeViews",
            label: t("clientView.stats.completeViews"),
            value: (d: ClientPortalDetailDay) =>
              formatNumber(d.completeViews, language),
          },
        ]
      : []),
    ...(s.conversions > 0
      ? [
          {
            key: "conversions",
            label: t("clientView.stats.conversions"),
            value: (d: ClientPortalDetailDay) =>
              formatNumber(d.conversions, language),
          },
        ]
      : []),
    ...(s.engagements > 0
      ? [
          {
            key: "engagements",
            label: t("clientView.stats.engagements"),
            value: (d: ClientPortalDetailDay) =>
              formatNumber(d.engagements, language),
          },
        ]
      : []),
    ...(s.investment > 0
      ? [
          {
            key: "investment",
            label: t("clientView.stats.investment"),
            value: (d: ClientPortalDetailDay) =>
              formatUnitMoney(d.investment, currency, language),
          },
          ...costColumns,
        ]
      : []),
  ];
  const grid = {
    display: "grid",
    gridTemplateColumns: `minmax(120px, 1.2fr) repeat(${columns.length}, minmax(100px, 1fr))`,
    columnGap: 2,
    alignItems: "center",
  } as const;
  const days = [...detail.daily].reverse();

  return (
    <ClientCard
      title={t("clientView.detail.tableTitle")}
      subtitle={t("clientView.detail.tableSubtitle")}
    >
      <Box
        sx={{
          maxHeight: 520,
          overflow: "auto",
          mx: { xs: -2.5, md: -3.5 },
          px: { xs: 2.5, md: 3.5 },
        }}
      >
        <Box sx={{ minWidth: 120 + columns.length * 110 }}>
          <Box
            sx={{
              ...grid,
              position: "sticky",
              top: 0,
              zIndex: 1,
              py: 1.5,
              px: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {[t("clientView.detail.date"), ...columns.map((c) => c.label)].map(
              (label, index) => (
                <Typography
                  key={label}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "text.secondary",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    textAlign: index === 0 ? "left" : "right",
                  }}
                >
                  {label}
                </Typography>
              )
            )}
          </Box>
          {days.map((d) => (
            <Box
              key={d.date}
              sx={{
                ...grid,
                py: 1.25,
                px: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                fontSize: 13,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {formatDate(d.date, language)}
              </Typography>
              {columns.map((c) => (
                <Typography
                  key={c.key}
                  sx={{
                    fontSize: 13,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {c.value(d)}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </ClientCard>
  );
};

// ─── Todo junto ──────────────────────────────────────────────────────────────

/**
 * Todos los apartados de detalle, en orden: resultados, retención, anuncios,
 * dónde se ha visto, audiencia, pantallas y la tabla por día. El día a día va
 * en las gráficas de la página (MetricChartsViewClient).
 */
/**
 * El detalle en unas fechas: los días de dentro y, en el resumen, lo que se
 * puede sumar día a día. Alcance, reproducciones, pases y cuartiles solo los
 * dan las plataformas para toda la campaña: se quedan como están.
 */
const detailInRange = (
  detail: ClientPortalDetail,
  from: string,
  to: string
): ClientPortalDetail => {
  const daily = detail.daily.filter((d) => d.date >= from && d.date <= to);
  const sum = (key: keyof Omit<ClientPortalDetailDay, "date">) =>
    daily.reduce((total, d) => total + d[key], 0);
  return {
    ...detail,
    daily,
    summary: {
      ...detail.summary,
      investment: sum("investment"),
      impressions: sum("impressions"),
      clicks: sum("clicks"),
      completeViews: sum("completeViews"),
      conversions: sum("conversions"),
      engagements: sum("engagements"),
      measurableImpressions: sum("measurableImpressions"),
      viewableImpressions: sum("viewableImpressions"),
    },
  };
};

/** Aviso de que unos apartados son de toda la campaña, no de las fechas. */
const WholeCampaignNote: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Typography
      sx={{ fontSize: 13, color: "text.secondary", textAlign: "center" }}
    >
      {t("clientView.range.wholeCampaignNote")}
    </Typography>
  );
};

const DetailSectionsViewClient: React.FC<{
  detail: ClientPortalDetail;
  /** Moneda de la campaña, para la inversión */
  currency: string;
  /** Fechas de la página: el día a día y lo que se suma por día, de ellas. */
  from?: string;
  to?: string;
  /** Las fechas son toda la campaña (sin aviso). */
  lifetime?: boolean;
  /** Google Ads: en el día a día, CPC y CPA en vez de CPM y CPC. */
  googleAds?: boolean;
}> = ({ detail: whole, currency, from, to, lifetime = true, googleAds }) => {
  const detail = from && to ? detailInRange(whole, from, to) : whole;
  return (
    <>
      <ResultsSection detail={detail} currency={currency} />
      {!lifetime && <WholeCampaignNote />}
      <RetentionSection detail={detail} />
      <CreativesSection creatives={detail.creatives} currency={currency} />
      <PlacementsSection placements={detail.placements} currency={currency} />
      <AudienceSection detail={detail} currency={currency} />
      <MapSection detail={detail} />
      <DailyTableSection
        detail={detail}
        currency={currency}
        googleAds={googleAds}
      />
    </>
  );
};

export default DetailSectionsViewClient;
